import { TrackingService } from '../services/TrackingService'
import type { FormContext, StepContext } from '../types'
import { FormViewed } from '../events/FormViewed'

export interface UseFormViewedProps {
  formContext: FormContext
  stepContext: StepContext
}

/**
 * Hook for tracking form viewed events
 *
 * Tracks when a user views a step in a multi-step form.
 * This is useful for understanding form viewed rates
 *
 * @returns A function that tracks form viewed events
 *
 * @example
 * ```typescript
 * const trackFormViewed = useFormViewed()
 *
 * const handleStepViewed = () => {
 *   trackFormViewed({
 *     formContext: {
 *       formName: 'signup',
 *       formId: 'form_123',
 *       formType: 'multi-step'
 *     },
 *     stepContext: {
 *       stepId: '1',
 *       stepName: 'Step 1',
 *       stepNumber: 1,
 *     },
 *   })
 * }
 * ```
 */
const useFormViewed =
  () =>
  (props: UseFormViewedProps): void => {
    const { formContext, stepContext } = props

    if (!formContext || !stepContext) {
      console.error('Missing required parameters for form viewed.')
      return
    }

    const formViewedEventPayload = new FormViewed({
      formContext,
      stepContext,
    })

    TrackingService.track(formViewedEventPayload)
  }

export default useFormViewed
